/**
 * Currency Detection Utilities
 * Detects currency and locale from browser and manages caching
 */

const CURRENCY_CACHE_KEY = 'userCurrency';
const LOCALE_CACHE_KEY = 'userLocale';
const COUNTRY_CACHE_KEY = 'userCountry';

// Currency mapping by country code
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  AE: 'AED', // UAE
  SA: 'SAR', // Saudi Arabia
  QA: 'QAR', // Qatar
  KW: 'KWD', // Kuwait
  BH: 'BHD', // Bahrain
  OM: 'OMR', // Oman
  EG: 'EGP', // Egypt
  US: 'USD', // United States
  GB: 'GBP', // United Kingdom
  EU: 'EUR', // European Union (not a country, but used)
  CA: 'CAD', // Canada
  AU: 'AUD', // Australia
  JP: 'JPY', // Japan
  CN: 'CNY', // China
  IN: 'INR', // India
  FR: 'EUR', // France
  DE: 'EUR', // Germany
  IT: 'EUR', // Italy
  ES: 'EUR', // Spain
  NL: 'EUR', // Netherlands
  BE: 'EUR', // Belgium
  AT: 'EUR', // Austria
  PT: 'EUR', // Portugal
  IE: 'EUR', // Ireland
  FI: 'EUR', // Finland
  GR: 'EUR', // Greece
  LU: 'EUR', // Luxembourg
  MT: 'EUR', // Malta
  CY: 'EUR', // Cyprus
  SK: 'EUR', // Slovakia
  SI: 'EUR', // Slovenia
  EE: 'EUR', // Estonia
  LV: 'EUR', // Latvia
  LT: 'EUR', // Lithuania
};

// Locale to country mapping (common patterns)
const LOCALE_TO_COUNTRY: Record<string, string> = {
  'en-US': 'US',
  'en-GB': 'GB',
  'en-AU': 'AU',
  'en-CA': 'CA',
  'ar-AE': 'AE',
  'ar-SA': 'SA',
  'ar-QA': 'QA',
  'ar-KW': 'KW',
  'ar-BH': 'BH',
  'ar-OM': 'OM',
  'ar-EG': 'EG',
  'ja-JP': 'JP',
  'zh-CN': 'CN',
  'hi-IN': 'IN',
  'fr-FR': 'FR',
  'de-DE': 'DE',
  'it-IT': 'IT',
  'es-ES': 'ES',
  'nl-NL': 'NL',
  'pt-PT': 'PT',
};

// Timezone to country mapping (major timezones)
const TIMEZONE_TO_COUNTRY: Record<string, string> = {
  'America/New_York': 'US',
  'America/Los_Angeles': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Toronto': 'CA',
  'Europe/London': 'GB',
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL',
  'Asia/Dubai': 'AE',
  'Asia/Riyadh': 'SA',
  'Asia/Qatar': 'QA',
  'Asia/Kuwait': 'KW',
  'Asia/Bahrain': 'BH',
  'Asia/Muscat': 'OM',
  'Africa/Cairo': 'EG',
  'Asia/Tokyo': 'JP',
  'Asia/Shanghai': 'CN',
  'Asia/Kolkata': 'IN',
  'Australia/Sydney': 'AU',
  'Australia/Melbourne': 'AU',
};

/**
 * Extract country code from locale string
 * Examples: 'en-US' -> 'US', 'ar-AE' -> 'AE', 'en' -> null
 */
export function detectCountryFromLocale(locale: string): string | null {
  if (!locale) return null;

  // Check direct mapping first
  if (LOCALE_TO_COUNTRY[locale]) {
    return LOCALE_TO_COUNTRY[locale];
  }

  // Extract country code from locale (e.g., 'en-US' -> 'US')
  const parts = locale.split('-');
  if (parts.length >= 2) {
    const countryCode = parts[1].toUpperCase();
    // Validate it's a 2-letter country code
    if (countryCode.length === 2 && /^[A-Z]{2}$/.test(countryCode)) {
      return countryCode;
    }
  }

  return null;
}

/**
 * Detect country from timezone
 */
export function detectCountryFromTimezone(timezone: string): string | null {
  if (!timezone) return null;
  return TIMEZONE_TO_COUNTRY[timezone] || null;
}

/**
 * Get currency code for a country
 */
export function getCurrencyForCountry(countryCode: string | null): string {
  if (!countryCode) return 'USD';
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || 'USD';
}

/**
 * Detect currency from locale
 */
export function detectCurrencyFromLocale(locale: string): string {
  const country = detectCountryFromLocale(locale);
  return getCurrencyForCountry(country);
}

/**
 * Get cached currency from localStorage
 */
export function getCachedCurrency(): string {
  try {
    const cached = localStorage.getItem(CURRENCY_CACHE_KEY);
    return cached || 'USD';
  } catch (error) {
    console.error('Failed to get cached currency:', error);
    return 'USD';
  }
}

/**
 * Cache currency in localStorage
 */
export function setCachedCurrency(currency: string): void {
  try {
    localStorage.setItem(CURRENCY_CACHE_KEY, currency.toUpperCase());
  } catch (error) {
    console.error('Failed to cache currency:', error);
  }
}

/**
 * Get cached locale from localStorage
 */
export function getCachedLocale(): string {
  try {
    const cached = localStorage.getItem(LOCALE_CACHE_KEY);
    return cached || 'en';
  } catch (error) {
    console.error('Failed to get cached locale:', error);
    return 'en';
  }
}

/**
 * Cache locale in localStorage
 */
export function setCachedLocale(locale: string): void {
  try {
    localStorage.setItem(LOCALE_CACHE_KEY, locale);
  } catch (error) {
    console.error('Failed to cache locale:', error);
  }
}

/**
 * Get cached country from localStorage
 */
export function getCachedCountry(): string {
  try {
    const cached = localStorage.getItem(COUNTRY_CACHE_KEY);
    return cached || 'US';
  } catch (error) {
    console.error('Failed to get cached country:', error);
    return 'US';
  }
}

/**
 * Cache country in localStorage
 */
export function setCachedCountry(country: string): void {
  try {
    localStorage.setItem(COUNTRY_CACHE_KEY, country.toUpperCase());
  } catch (error) {
    console.error('Failed to cache country:', error);
  }
}

/**
 * Detect currency and locale from browser
 * Priority: URL params > localStorage > browser detection > defaults
 */
export function detectFromBrowser(): {
  currency: string;
  locale: string;
  country: string;
} {
  // Check URL parameters first (for redirects from website)
  const urlParams = new URLSearchParams(window.location.search);
  const urlCurrency = urlParams.get('currency');
  const urlLocale = urlParams.get('locale');
  const urlCountry = urlParams.get('country');

  if (urlCurrency || urlLocale || urlCountry) {
    const currency = urlCurrency?.toUpperCase() || getCachedCurrency();
    const locale = urlLocale || getCachedLocale();
    const country = urlCountry?.toUpperCase() || getCachedCountry();

    // Cache the values from URL
    if (urlCurrency) setCachedCurrency(currency);
    if (urlLocale) setCachedLocale(locale);
    if (urlCountry) setCachedCountry(country);

    return { currency, locale, country };
  }

  // Check localStorage cache
  const cachedCurrency = getCachedCurrency();
  const cachedLocale = getCachedLocale();
  const cachedCountry = getCachedCountry();

  if (cachedCurrency !== 'USD' || cachedLocale !== 'en' || cachedCountry !== 'US') {
    return {
      currency: cachedCurrency,
      locale: cachedLocale,
      country: cachedCountry,
    };
  }

  // Detect from browser
  const browserLocale = navigator.language || navigator.languages?.[0] || 'en';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Detect country
  let detectedCountry = detectCountryFromLocale(browserLocale);
  if (!detectedCountry) {
    detectedCountry = detectCountryFromTimezone(timezone);
  }
  const country = detectedCountry || 'US';

  // Detect currency
  const currency = detectCurrencyFromLocale(browserLocale) || getCurrencyForCountry(country);

  // Use browser locale or default
  const locale = browserLocale || 'en';

  // Cache detected values
  setCachedCurrency(currency);
  setCachedLocale(locale);
  setCachedCountry(country);

  return { currency, locale, country };
}


